import { useQuery } from '@tanstack/react-query'
import { useLatestBidSubmissions } from 'api/elplan/bidSubmissions.api'
import type { TradeResultSyncConfig } from 'api/elplan/elplan.types'
import { TRADE_RESULT_SYNC_QUERY_KEY } from 'api/elplan/intradayTrading.api'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { HourlyDataTable } from 'ui/components/HourlyDataTable/HourlyDataTable'
import { getCellDisplay } from 'ui/components/HourlyDataTable/HourlyDataTable.cellEdit.helper'
import hourlyStyles from 'ui/components/HourlyDataTable/HourlyDataTable.module.less'
import type { TableCellEditRule } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { useTableCellClipboard } from 'ui/components/HourlyDataTable/hooks/useTableCellClipboard'
import { useTableCellEdit } from 'ui/components/HourlyDataTable/hooks/useTableCellEdit'
import { useTableCellNavigation } from 'ui/components/HourlyDataTable/hooks/useTableCellNavigation'
import { useTableCellSelection } from 'ui/components/HourlyDataTable/hooks/useTableCellSelection'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { fetchDatasetsFromDbS } from 'utils/dbs/dbs'
import { parseNonNegativeDecimal } from 'utils/numberUtils/decimalInput'
import { addLocaleSeparators } from 'utils/numberUtils/numberUtils'
import { getPrefillWindow } from '../../helpers/intradayBidSubmissions.helper'
import styles from '../../IntradayTradingView.module.less'
import { BidInputCell } from './BidInputCell'
import type { CellColumn, MouseEventLike, RowValues, UnitColumn } from './BidInputTable.types'
import {
  getActiveSubtypeTags,
  getCellKey,
  getConfiguredUnitColumns,
  parseCellKey,
} from './helpers/BidInputTable.helpers'
import { getBidHourRangeLabels, getBidRowDateLabel, getBidTimestamp } from './helpers/BidInputTable.time'
import {
  lookupTradeResultValue,
  type ResolvedPositionDatasets,
  resolveAllPositionDatasets,
} from './helpers/BidInputTable.tradeResultSync'
import { isBidValueInvalid, isConfiguredPositionsValid } from './helpers/BidInputValidation.helper'
import { makeBidTableAdapter } from './tableCellAdapters/bidTableAdapter'

const BID_VOLUME_RULE: TableCellEditRule = { kind: 'number', admitsSign: false }

type BidInputTableEligibility = {
  canSubmit: boolean
  disabledReason: string
}

type BidInputTableProps = {
  configuredPositions: Array<{
    position_id: string
    display_name?: string
    tags: Array<{
      tag_id: string
      display_name: string
      enabled?: boolean
      buy: Record<string, unknown>
      sell: Record<string, unknown>
    }>
  }>
  onEligibilityChange: (eligibility: BidInputTableEligibility) => void
  onPayloadChange: (
    positions: Array<{
      position_id: string
      tags: Array<{
        tag_id: string
        buy?: Array<{ time: ISODateTime; value: number }>
        sell?: Array<{ time: ISODateTime; value: number }>
      }>
    }>
  ) => void
  secondaryAction?: ReactNode
  topRightAction?: ReactNode
  activeSubtype?: string
  systemId?: number
  tradeResultSyncConfig?: TradeResultSyncConfig
  onTradeResultFetched?: () => void
  onTradeResultFetchingChange?: (isFetching: boolean) => void
  bidBaseStartTime: Date
  rowCount: number
  onAddRow: () => void
  onRemoveLastRow: () => void
}

type IntradayBidTagPayload = {
  tag_id: string
  buy?: Array<{ time: ISODateTime; value: number }>
  sell?: Array<{ time: ISODateTime; value: number }>
}

type BidInputRowProps = {
  hourIndex: number
  renderedColumns: UnitColumn[]
  totalColumns: UnitColumn[]
  rowValues: RowValues
  selectedCellKeys: Set<string>
  t: (key: string) => string
  onValueChange: (cellKey: string, value: string) => void
  /** Generalized signature: cellKey is derived from (hourIndex, position, tag, direction) in BidInputRow */
  onCellKeyDown: (cellKey: string, ev: ReactKeyboardEvent) => void
  onCellMouseDown: (cellKey: string, ev: MouseEventLike) => void
  onCellMouseOver: (cellKey: string) => void
  registerInputRef: (cellKey: string, element: HTMLInputElement | null) => void
  tradeResultCells?: Array<{
    positionId: string
    tradedVolumeDisplay: string
    optimizationInputDisplay: string
  }>
}

const BidInputRow = memo(function BidInputRow({
  hourIndex,
  renderedColumns,
  totalColumns,
  rowValues,
  selectedCellKeys,
  t,
  onValueChange,
  onCellKeyDown,
  onCellMouseDown,
  onCellMouseOver,
  registerInputRef,
  tradeResultCells,
}: BidInputRowProps) {
  const rowTotal = totalColumns.reduce((total, column) => {
    const buyRaw = rowValues[getCellKey(hourIndex, column.positionId, column.tagId, 'buy')] ?? ''
    const sellRaw = rowValues[getCellKey(hourIndex, column.positionId, column.tagId, 'sell')] ?? ''
    const buyValue = parseNonNegativeDecimal(buyRaw) ?? 0
    const sellValue = parseNonNegativeDecimal(sellRaw) ?? 0
    return total + buyValue - sellValue
  }, 0)

  return (
    <>
      {renderedColumns.flatMap((column) => {
        const buyCellKey = getCellKey(hourIndex, column.positionId, column.tagId, 'buy')
        const sellCellKey = getCellKey(hourIndex, column.positionId, column.tagId, 'sell')
        const buyCellRaw = rowValues[buyCellKey] ?? ''
        const sellCellRaw = rowValues[sellCellKey] ?? ''
        const buyCellDisplay = getCellDisplay(buyCellRaw, BID_VOLUME_RULE)
        const sellCellDisplay = getCellDisplay(sellCellRaw, BID_VOLUME_RULE)
        const buyInvalid = isBidValueInvalid(buyCellRaw)
        const sellInvalid = isBidValueInvalid(sellCellRaw)

        return [
          <BidInputCell
            key={buyCellKey}
            direction="buy"
            hourIndex={hourIndex}
            positionId={column.positionId}
            tagId={column.tagId}
            displayValue={buyCellDisplay}
            isSelected={selectedCellKeys.has(buyCellKey)}
            isInvalid={buyInvalid}
            isGroupStart={true}
            ariaLabel={t('Buy volume input')}
            onValueChange={(value) => onValueChange(buyCellKey, value)}
            onKeyDown={(ev) => onCellKeyDown(buyCellKey, ev)}
            onMouseDown={(ev) => onCellMouseDown(buyCellKey, ev)}
            onMouseOver={() => onCellMouseOver(buyCellKey)}
            registerRef={(el) => registerInputRef(buyCellKey, el)}
          />,
          <BidInputCell
            key={sellCellKey}
            direction="sell"
            hourIndex={hourIndex}
            positionId={column.positionId}
            tagId={column.tagId}
            displayValue={sellCellDisplay}
            isSelected={selectedCellKeys.has(sellCellKey)}
            isInvalid={sellInvalid}
            isGroupStart={false}
            ariaLabel={t('Sell volume input')}
            onValueChange={(value) => onValueChange(sellCellKey, value)}
            onKeyDown={(ev) => onCellKeyDown(sellCellKey, ev)}
            onMouseDown={(ev) => onCellMouseDown(sellCellKey, ev)}
            onMouseOver={() => onCellMouseOver(sellCellKey)}
            registerRef={(el) => registerInputRef(sellCellKey, el)}
          />,
        ]
      })}
      <td
        className={[
          styles.IntradayTradingView_BidTableTotalCell,
          hourlyStyles.HourlyDataTable_BidTableTotalBoundaryCell,
          (tradeResultCells?.length ?? 0) > 0
            ? hourlyStyles.HourlyDataTable_BidTableStickyRightCell3
            : hourlyStyles.HourlyDataTable_BidTableStickyRightCell,
        ].join(' ')}
      >
        {addLocaleSeparators(rowTotal)}
      </td>
      {tradeResultCells?.map((cell, cellIndex) => {
        const isLast = cellIndex === (tradeResultCells?.length ?? 0) - 1
        return (
          <Fragment key={`trade-result-sync-${cell.positionId}`}>
            <td
              className={[
                styles.IntradayTradingView_BidTableTotalCell,
                hourlyStyles.HourlyDataTable_BidTableTotalBoundaryCell,
                isLast ? hourlyStyles.HourlyDataTable_BidTableStickyRightCell2 : '',
              ].join(' ')}
            >
              {cell.tradedVolumeDisplay}
            </td>
            <td
              className={[
                styles.IntradayTradingView_BidTableTotalCell,
                hourlyStyles.HourlyDataTable_BidTableTotalBoundaryCell,
                isLast ? hourlyStyles.HourlyDataTable_BidTableStickyRightCell : '',
              ].join(' ')}
            >
              {cell.optimizationInputDisplay}
            </td>
          </Fragment>
        )
      })}
    </>
  )
})

export function BidInputTable({
  configuredPositions,
  onEligibilityChange,
  onPayloadChange,
  secondaryAction,
  topRightAction,
  activeSubtype,
  systemId,
  tradeResultSyncConfig,
  onTradeResultFetched,
  onTradeResultFetchingChange,
  bidBaseStartTime,
  rowCount,
  onAddRow: handleAddRow,
  onRemoveLastRow: removeLastRow,
}: BidInputTableProps) {
  const { t } = useTranslation()
  const safeConfiguredPositions = isConfiguredPositionsValid(configuredPositions) ? configuredPositions : []

  // Fetch bid submission history for pre-fill, derived from stable bidBaseStartTime
  const { startTime, endTime } = getPrefillWindow(bidBaseStartTime)
  const hasSystemId = systemId !== undefined
  const {
    data: bidSubmissionRowValues,
    isPending: isBidSubmissionPending,
    isLoading: isBidSubmissionLoadingLegacy,
    error: bidSubmissionError,
  } = useLatestBidSubmissions(systemId ?? 0, startTime, endTime)
  const isBidSubmissionLoading = isBidSubmissionPending || isBidSubmissionLoadingLegacy

  const prefillData = hasSystemId ? bidSubmissionRowValues : undefined
  const prefillDataRef = useRef(prefillData)

  const positionResolutions = useMemo(
    (): ResolvedPositionDatasets[] =>
      tradeResultSyncConfig ? resolveAllPositionDatasets(tradeResultSyncConfig, activeSubtype) : [],
    [tradeResultSyncConfig, activeSubtype]
  )

  // Only render trade result sync columns when every position resolved successfully.
  // If any position is misconfigured the banner is shown and columns are hidden.
  const renderablePositions = useMemo(
    () =>
      positionResolutions.some((r) => r.status === 'misconfigured')
        ? []
        : positionResolutions.filter(
            (r): r is Extract<ResolvedPositionDatasets, { status: 'ok' }> => r.status === 'ok'
          ),
    [positionResolutions]
  )

  const allDatasetIds = useMemo(
    () =>
      [...new Set(renderablePositions.flatMap((r) => [r.tradedVolumeDatasetId, r.optimizationInputDatasetId]))].sort(
        (a, b) => a - b
      ),
    [renderablePositions]
  )

  const {
    data: tradeResultDatasets = [],
    isLoading: isTradeResultLoading,
    dataUpdatedAt,
    isFetching: isTradeResultFetching,
  } = useQuery({
    queryKey: [TRADE_RESULT_SYNC_QUERY_KEY, allDatasetIds, startTime, endTime],
    queryFn: () =>
      fetchDatasetsFromDbS([
        {
          type: 'dbs',
          filter: { start_time: startTime, end_time: endTime },
          contract: {
            // metadata_filter is required by the type but dataset_ids takes precedence;
            // empty include/exclude means no metadata filter is applied by the DBS client.
            metadata_filter: { include: {}, exclude: {} },
            dataset_ids: allDatasetIds,
            use_dbs_ids_as_return_ids: true,
          },
        },
      ]),
    enabled: allDatasetIds.length > 0,
  })

  const onTradeResultFetchedRef = useRef(onTradeResultFetched)
  useEffect(() => {
    onTradeResultFetchedRef.current = onTradeResultFetched
  })

  useEffect(() => {
    if (dataUpdatedAt > 0) {
      onTradeResultFetchedRef.current?.()
    }
  }, [dataUpdatedAt])

  useEffect(() => {
    onTradeResultFetchingChange?.(isTradeResultFetching)
  }, [isTradeResultFetching, onTradeResultFetchingChange])

  const getTradeResultValue = useCallback(
    (datasetId: number, hourIndex: number): string =>
      lookupTradeResultValue(
        tradeResultDatasets,
        isTradeResultLoading,
        datasetId,
        getBidTimestamp(bidBaseStartTime, hourIndex)
      ),
    [tradeResultDatasets, isTradeResultLoading, bidBaseStartTime]
  )

  const hours = useMemo(() => Array.from({ length: rowCount }, (_unused, index) => index), [rowCount])
  const hourLabels = getBidHourRangeLabels(bidBaseStartTime, rowCount)

  const columns = useMemo<UnitColumn[]>(() => {
    return getConfiguredUnitColumns(safeConfiguredPositions, activeSubtype)
  }, [safeConfiguredPositions, activeSubtype])

  const [collapsedUnitKeys, setCollapsedUnitKeys] = useState<Set<string>>(new Set())
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterMenuRef = useRef<HTMLDivElement | null>(null)
  const firstFilterCheckboxRef = useRef<HTMLInputElement | null>(null)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)

  const visibleColumns = useMemo(
    () => columns.filter((column) => !collapsedUnitKeys.has(column.unitKey)),
    [columns, collapsedUnitKeys]
  )

  const [valuesByHour, setValuesByHour] = useState<RowValues[]>(() => {
    const emptyRows = Array.from({ length: rowCount }, () => ({}))
    if (prefillData && prefillData.length > 0) {
      return emptyRows.map((emptyRow, index) => {
        const prefillRow = prefillData[index] ?? {}
        return { ...prefillRow, ...emptyRow }
      })
    }
    return emptyRows
  })
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const previousSystemIdRef = useRef<number | undefined>(systemId)
  const valuesByHourRef = useRef<RowValues[]>([])

  const visibleCellColumns = useMemo<CellColumn[]>(() => {
    return visibleColumns.flatMap((column) => [
      { positionId: column.positionId, tagId: column.tagId, direction: 'buy' as const },
      { positionId: column.positionId, tagId: column.tagId, direction: 'sell' as const },
    ])
  }, [visibleColumns])

  // Keep valuesByHourRef in sync for async clipboard handlers
  useEffect(() => {
    valuesByHourRef.current = valuesByHour
  }, [valuesByHour])

  // Apply prefill data when it arrives from the query
  useEffect(() => {
    if (prefillData && prefillData.length > 0) {
      setValuesByHour((prevValues) =>
        prevValues.map((row, index) => {
          const prefillRow = prefillData[index] ?? {}
          const merged: RowValues = { ...row }
          for (const [key, value] of Object.entries(prefillRow)) {
            if (!(key in merged)) {
              merged[key] = value
            }
          }
          return merged
        })
      )
    }
  }, [prefillData])

  const adapter = useMemo(() => makeBidTableAdapter(visibleCellColumns, rowCount), [visibleCellColumns, rowCount])

  // ---------------------------------------------------------------------------
  // Selection hook
  // ---------------------------------------------------------------------------

  const {
    selectedCellKeys,
    selectedCellKeysRef,
    selectedCellKeysForClipboardRef,
    anchorCellKeyForClipboardRef,
    bulkReplaceOnNextInputRef,
    setAnchorCellKey,
    replaceSelection,
    clearSelection,
    handleCellMouseDown,
    handleCellMouseOver,
    getEditTargets,
    takeReplaceOnType,
  } = useTableCellSelection({ adapter, tableContainerRef })

  // Reset table (including selection) when the active system changes
  useEffect(() => {
    if (previousSystemIdRef.current === systemId) {
      return
    }

    previousSystemIdRef.current = systemId
    setValuesByHour((prev) => Array.from({ length: prev.length }, () => ({})))
    clearSelection()
  }, [systemId, clearSelection])

  // ---------------------------------------------------------------------------
  // Value state helpers
  // ---------------------------------------------------------------------------

  const clearBidInputValuesForSelection = useCallback((cellKeysToClear: Set<string>) => {
    if (cellKeysToClear.size === 0) {
      return
    }

    setValuesByHour((previous) => {
      const next = [...previous]

      cellKeysToClear.forEach((selectedCellKey) => {
        const parsedCell = parseCellKey(selectedCellKey)

        if (!parsedCell || parsedCell.hourIndex < 0 || parsedCell.hourIndex >= next.length) {
          return
        }

        next[parsedCell.hourIndex] = {
          ...(next[parsedCell.hourIndex] ?? {}),
          [selectedCellKey]: '',
        }
      })

      return next
    })
  }, [])

  // ---------------------------------------------------------------------------
  // Navigation hook
  // ---------------------------------------------------------------------------

  const { handleCellKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
    adapter,
    clearValuesForSelection: clearBidInputValuesForSelection,
    clearSelection,
    replaceSelection,
    setAnchorCellKey,
    inputRefs,
    bulkReplaceOnNextInputRef,
    selectedCellKeysRef,
  })

  const { handleCellValueChange, handleCellKeyDown } = useTableCellEdit({
    adapter,
    inputRefs,
    valuesByRow: valuesByHour,
    setValuesByRow: setValuesByHour,
    getCellRule: () => BID_VOLUME_RULE,
    getEditTargets,
    takeReplaceOnType,
    handleNavigationKeyDown,
  })

  // ---------------------------------------------------------------------------
  // Clipboard hook
  // ---------------------------------------------------------------------------

  useTableCellClipboard({
    tableContainerRef,
    adapter,
    anchorCellKeyForClipboardRef,
    selectedCellKeysForClipboardRef,
    valuesByRowRef: valuesByHourRef,
    setValuesByRow: setValuesByHour,
  })

  const registerInputRef = useCallback((cellKey: string, element: HTMLInputElement | null) => {
    if (element === null) {
      delete inputRefs.current[cellKey]
      return
    }

    inputRefs.current[cellKey] = element
  }, [])

  // ---------------------------------------------------------------------------
  // Row management
  // ---------------------------------------------------------------------------

  useEffect(() => {
    prefillDataRef.current = prefillData
  })

  useEffect(() => {
    setValuesByHour((previous) => {
      if (previous.length === rowCount) {
        return previous
      }

      if (previous.length < rowCount) {
        const currentPrefill = prefillDataRef.current
        return [
          ...previous,
          ...Array.from({ length: rowCount - previous.length }, (_, offset) => {
            const hourIndex = previous.length + offset
            return currentPrefill?.[hourIndex] ?? {}
          }),
        ]
      }

      return previous.slice(0, rowCount)
    })
  }, [rowCount])

  // ---------------------------------------------------------------------------
  // Column visibility toggle
  // ---------------------------------------------------------------------------

  const toggleUnit = useCallback((unitKey: string) => {
    setCollapsedUnitKeys((previous) => {
      const next = new Set(previous)
      if (next.has(unitKey)) {
        next.delete(unitKey)
      } else {
        next.add(unitKey)
      }
      return next
    })
  }, [])

  // ---------------------------------------------------------------------------
  // Filter menu
  // ---------------------------------------------------------------------------

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent): void {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setIsFilterMenuOpen(false)
      }
    }

    function handleEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsFilterMenuOpen(false)
      }
    }

    if (isFilterMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isFilterMenuOpen])

  useEffect(() => {
    if (isFilterMenuOpen && firstFilterCheckboxRef.current) {
      firstFilterCheckboxRef.current.focus()
    }
  }, [isFilterMenuOpen])

  // ---------------------------------------------------------------------------
  // Eligibility and payload propagation
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const allEntries = columns.flatMap((column) =>
      hours.flatMap((hourIndex) => [
        valuesByHour[hourIndex]?.[getCellKey(hourIndex, column.positionId, column.tagId, 'buy')] ?? '',
        valuesByHour[hourIndex]?.[getCellKey(hourIndex, column.positionId, column.tagId, 'sell')] ?? '',
      ])
    )
    const enteredEntries = allEntries.filter((value) => value.trim() !== '')
    const hasAnyEntered = enteredEntries.length > 0
    const hasInvalidEntry = allEntries.some(isBidValueInvalid)

    if (!hasAnyEntered) {
      onEligibilityChange({
        canSubmit: false,
        disabledReason: t('Enter at least one bid volume before submitting.'),
      })
      return
    }

    if (hasInvalidEntry) {
      onEligibilityChange({
        canSubmit: false,
        disabledReason: t('Remove invalid or negative values before submitting.'),
      })
      return
    }

    onEligibilityChange({ canSubmit: true, disabledReason: '' })
  }, [onEligibilityChange, t, valuesByHour, columns, hours])

  useEffect(() => {
    const payload = safeConfiguredPositions
      .map((position) => {
        const tags = getActiveSubtypeTags(position, activeSubtype).map((tag) => {
          const buy = hours
            .map((hourIndex) => {
              const buyParsed = parseNonNegativeDecimal(
                valuesByHour[hourIndex]?.[getCellKey(hourIndex, position.position_id, tag.tag_id, 'buy')] ?? ''
              )

              if (buyParsed === null) {
                return null
              }

              return { time: getBidTimestamp(bidBaseStartTime, hourIndex), value: buyParsed }
            })
            .filter((value): value is { time: ISODateTime; value: number } => value !== null)

          const sell = hours
            .map((hourIndex) => {
              const sellParsed = parseNonNegativeDecimal(
                valuesByHour[hourIndex]?.[getCellKey(hourIndex, position.position_id, tag.tag_id, 'sell')] ?? ''
              )

              if (sellParsed === null) {
                return null
              }

              return { time: getBidTimestamp(bidBaseStartTime, hourIndex), value: sellParsed }
            })
            .filter((value): value is { time: ISODateTime; value: number } => value !== null)

          const payloadTag: IntradayBidTagPayload = { tag_id: tag.tag_id }

          if (buy.length > 0) {
            payloadTag.buy = buy
          }

          if (sell.length > 0) {
            payloadTag.sell = sell
          }

          return payloadTag
        })

        return { position_id: position.position_id, tags }
      })
      .filter((position) => position.tags.length > 0)

    onPayloadChange(payload)
  }, [bidBaseStartTime, safeConfiguredPositions, hours, onPayloadChange, valuesByHour, activeSubtype])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <div className={styles.IntradayTradingView_BidGridFilterRoot} ref={filterMenuRef}>
          <button
            type="button"
            className={styles.IntradayTradingView_BidGridFilterButton}
            onClick={() => setIsFilterMenuOpen((open) => !open)}
            aria-expanded={isFilterMenuOpen}
            aria-haspopup="menu"
            aria-label={t('Filter columns')}
          >
            <i className="fal fa-filter" aria-hidden="true" />
            <span>{t('Filter')}</span>
          </button>
          {isFilterMenuOpen ? (
            <div
              className={styles.IntradayTradingView_BidGridColumnMenuList}
              role="menu"
              aria-label={t('Column filter')}
            >
              {columns.map((column, index) => {
                const isVisible = !collapsedUnitKeys.has(column.unitKey)

                return (
                  <label key={column.unitKey} className={styles.IntradayTradingView_BidGridColumnMenuItem}>
                    <input
                      ref={index === 0 ? firstFilterCheckboxRef : null}
                      type="checkbox"
                      checked={isVisible}
                      onChange={() => toggleUnit(column.unitKey)}
                      aria-label={`${isVisible ? t('Hide') : t('Show')} ${column.unitLabel}`}
                    />
                    <span>{column.unitLabel}</span>
                  </label>
                )
              })}
            </div>
          ) : null}
        </div>
        {topRightAction ? <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{topRightAction}</div> : null}
      </div>
      {columns.length === 0 && configuredPositions.some((pos) => (pos.tags ?? []).length > 0) ? (
        <div className={styles.IntradayTradingView_BidTableEmptyState} role="status" aria-live="polite">
          {t('No tags match the selected subtype')}
        </div>
      ) : null}
      {isBidSubmissionLoading && hasSystemId ? (
        <div className={styles.IntradayTradingView_BidTableEmptyState} role="status" aria-live="polite">
          {t('Loading bid submission history...')}
        </div>
      ) : null}
      {bidSubmissionError && !isBidSubmissionLoading && hasSystemId ? (
        <InfoBanner style="danger" text={t('Could not load bid submission history. Table will be empty.')} />
      ) : null}
      {positionResolutions.some((r) => r.status === 'misconfigured') ? (
        <InfoBanner
          style="danger"
          text={t('Trade result sync is misconfigured for one or more positions. Please contact support.')}
        />
      ) : null}
      <div className={styles.IntradayTradingView_BidTableLayout}>
        <HourlyDataTable
          rowCount={rowCount}
          tableContainerRef={tableContainerRef}
          getRowLabel={(i) => hourLabels[i]}
          getDateLabel={(i) => getBidRowDateLabel(bidBaseStartTime, i)}
          columnHeaders={
            <>
              {visibleColumns.map((column) => (
                <th key={column.unitKey} className={hourlyStyles.HourlyDataTable_BidTableGroupColumnHeader} colSpan={2}>
                  {column.unitLabel}
                </th>
              ))}
              <th
                className={[
                  hourlyStyles.HourlyDataTable_BidTableTh,
                  hourlyStyles.HourlyDataTable_BidTableTotalBoundaryHeader,
                  renderablePositions.length > 0
                    ? hourlyStyles.HourlyDataTable_BidTableStickyRightHeader3
                    : hourlyStyles.HourlyDataTable_BidTableStickyRightHeader,
                  hourlyStyles.HourlyDataTable_BidTableStickyTopPrimary,
                ].join(' ')}
                rowSpan={2}
              >
                {t('Total bid volume')}
              </th>
              {renderablePositions.map((res, posIndex) => {
                const isLast = posIndex === renderablePositions.length - 1
                return (
                  <Fragment key={`trade-result-sync-th-${res.positionId}`}>
                    <th
                      className={[
                        hourlyStyles.HourlyDataTable_BidTableTh,
                        hourlyStyles.HourlyDataTable_BidTableTotalBoundaryHeader,
                        hourlyStyles.HourlyDataTable_BidTableStickyTopPrimary,
                        isLast ? hourlyStyles.HourlyDataTable_BidTableStickyRightHeader2 : '',
                      ].join(' ')}
                      rowSpan={2}
                    >
                      {t('Traded volume')}
                    </th>
                    <th
                      className={[
                        hourlyStyles.HourlyDataTable_BidTableTh,
                        hourlyStyles.HourlyDataTable_BidTableTotalBoundaryHeader,
                        hourlyStyles.HourlyDataTable_BidTableStickyTopPrimary,
                        isLast ? hourlyStyles.HourlyDataTable_BidTableStickyRightHeader : '',
                      ].join(' ')}
                      rowSpan={2}
                    >
                      {t('Accepted trade result')}
                    </th>
                  </Fragment>
                )
              })}
            </>
          }
          subColumnHeaders={
            <>
              {visibleColumns.flatMap((column) => [
                <th
                  key={`${column.unitKey}-buy`}
                  className={`${hourlyStyles.HourlyDataTable_BidTableThSub} ${hourlyStyles.HourlyDataTable_BidTableGroupStartCell} ${hourlyStyles.HourlyDataTable_BidTableStickyTopSecondary}`}
                >
                  {t('Buy')}
                </th>,
                <th
                  key={`${column.unitKey}-sell`}
                  className={`${hourlyStyles.HourlyDataTable_BidTableThSub} ${hourlyStyles.HourlyDataTable_BidTableGroupEndCell} ${hourlyStyles.HourlyDataTable_BidTableStickyTopSecondary}`}
                >
                  {t('Sell')}
                </th>,
              ])}
            </>
          }
          renderCellsForRow={(hourIndex) => (
            <BidInputRow
              hourIndex={hourIndex}
              renderedColumns={visibleColumns}
              totalColumns={columns}
              rowValues={valuesByHour[hourIndex] ?? {}}
              selectedCellKeys={selectedCellKeys}
              t={t}
              onValueChange={handleCellValueChange}
              onCellKeyDown={handleCellKeyDown}
              onCellMouseDown={handleCellMouseDown}
              onCellMouseOver={handleCellMouseOver}
              registerInputRef={registerInputRef}
              tradeResultCells={
                renderablePositions.length > 0
                  ? renderablePositions.map((res) => ({
                      positionId: res.positionId,
                      tradedVolumeDisplay: getTradeResultValue(res.tradedVolumeDatasetId, hourIndex),
                      optimizationInputDisplay: getTradeResultValue(res.optimizationInputDatasetId, hourIndex),
                    }))
                  : undefined
              }
            />
          )}
        />
        {hours.length > 0 ? (
          <div className={styles.IntradayTradingView_BidTableAction}>
            <NewButton
              iconButton
              variant="delete"
              iconSize="small"
              onClick={() => {
                removeLastRow()
              }}
              tooltip={{
                title: t('Remove last row'),
                placement: 'top',
              }}
            />
          </div>
        ) : null}
      </div>
      <div className={styles.IntradayTradingView_BidTableActionRow}>
        <NewButton
          variant="outlined"
          label={t('Add more')}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            height: 'auto',
            lineHeight: 1.25,
            borderRadius: 0,
          }}
          onClick={() => {
            handleAddRow()
          }}
        />
        {secondaryAction ? (
          <div className={styles.IntradayTradingView_BidTableActionRowRight}>{secondaryAction}</div>
        ) : null}
      </div>
    </div>
  )
}
